import * as Icons from "@nova/component-icons"
import { ButtonPrimary, ButtonSecondary, ButtonTertiary, ButtonToggle } from "@nova/component-btn"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { Dropdown } from "@nova/component-dropdown"
import { ExpandableContent } from "@nova/component-expandable-content"
import { InputOutlined } from "@nova/ds-component-input"
import classNames from "classnames"
import { useCallback, useEffect, useState } from "react"
import { DistanceUnit, formattedDistanceUnit, Licenses } from "@nova/constants"
import { convertDistance } from "@nova/format"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { getViewState, setNewViewstate } from "features/map/GlobalViewState"
import {
    AircraftTypeKey,
    beginAdsbCirclePlacement,
    cancelAdsbCirclePlacement,
    confirmAdsbCircle,
    disableAdsb,
    enableAdsb,
    selectAdsbCircleCenter,
    selectAdsbEnabled,
    selectAdsbIsPlacingCircle,
    selectAdsbTrafficUpdatesAltitudeMaxFeet,
    selectAdsbTrafficUpdatesAltitudeMinFeet,
    selectAdsbTrafficUpdatesAltitudeUnit,
    selectAdsbTrafficUpdatesEnabled,
    selectAircraftTypeVisible,
    setAdsbTrafficUpdatesAltitudeMaxFeet,
    setAdsbTrafficUpdatesAltitudeMinFeet,
    setAdsbTrafficUpdatesAltitudeUnit,
    setAdsbTrafficUpdatesEnabled,
    setAircraftTypeVisible,
} from "../slice"
import { BetaTag } from "features/BetaTag"
import { selectIsDomainWhiteListed, selectUserLicense } from "user/profile/selectors"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { LicenseCard } from "features/data-management/workspace/subscriptions/modals/LicensePaywallModal"
import { LicenseDefinitions } from "features/data-management/workspace/subscriptions/module"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { setLicenseUpgrade } from "features/entity-caching/licenses/actions"
import { ModalNames } from "features/modals/types"

const adsbOptions = [
    {
        id: "fixedWing",
        icon: Icons.FixedWing,
        title: "Fixed-wing",
    },
    {
        id: "rotorcraft",
        icon: Icons.Helicopter,
        title: "Rotorcraft",
    },
    {
        id: "lta",
        icon: Icons.LTA,
        title: "Lighter than Air",
    },
    {
        id: "glider",
        icon: Icons.Glider,
        title: "Glider",
    },
    {
        id: "other",
        icon: Icons.Other,
        title: "Other",
    },
] satisfies { id: AircraftTypeKey; icon: Icons.IconComponent; title: string }[]

const ADSB_ALTITUDE_UNIT_OPTIONS = [DistanceUnit.Feet, DistanceUnit.Meters] as const

const ADSB_TARGET_ZOOM = 9
const ADSB_ZOOM_BUFFER = 0.5
const zoomToAdsbCircle = (center?: { lat: number; lng: number }) => {
    const viewState = getViewState()
    let newZoom = viewState.zoom
    let newPosition = { lat: viewState.latitude, lng: viewState.longitude }
    if (viewState.zoom < ADSB_TARGET_ZOOM - ADSB_ZOOM_BUFFER) {
        newZoom = ADSB_TARGET_ZOOM
    }
    if (viewState.zoom > ADSB_TARGET_ZOOM + ADSB_ZOOM_BUFFER) {
        newZoom = ADSB_TARGET_ZOOM
    }
    if (center) {
        newPosition = center
    }
    setNewViewstate({ zoom: newZoom, latitude: newPosition.lat, longitude: newPosition.lng })
}

export const AdsbSection = () => {
    const dispatch = useAppDispatch()
    const enabled = useAppSelector(selectAdsbEnabled)
    const isPlacingCircle = useAppSelector(selectAdsbIsPlacingCircle)
    const circleCenter = useAppSelector(selectAdsbCircleCenter)

    const license = useAppSelector(selectUserLicense)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    const canUseAdsb = license?.subscriptionType === Licenses.Pro || isWhitelisted
    const confirmationModal = useConfirmationModalContext()

    const toggleAdsb = () => {
        if (!canUseAdsb) {
            confirmationModal.confirm({
                RenderComponent: AdsbLicenseUpgradeModal,
            })
            return
        }
        if (enabled) {
            dispatch(disableAdsb())
            return
        }
        const { latitude, longitude } = getViewState()
        const fallbackCenter = { lat: latitude, lng: longitude }
        zoomToAdsbCircle(circleCenter ?? undefined)
        dispatch(enableAdsb({ fallbackCenter }))
    }

    return (
        <div className="flex flex-col">
            <div
                className="px-4 py-[14px] flex items-center justify-between cursor-pointer hover:bg-white-100"
                onClick={toggleAdsb}
            >
                <div className="flex items-center gap-2">
                    <div className="telegraf">ADS-B</div>
                    <BetaTag />
                </div>
                {canUseAdsb ? (
                    <ToggleSwitch isToggled={enabled} setIsToggled={() => {}} />
                ) : (
                    <div className="flex justify-center items-center rounded-md bg-blue-900 w-5 h-5 shrink-0">
                        <Icons.ExtraSmallIcon Icon={Icons.LayerLocked} className="text-white-400" />
                    </div>
                )}
            </div>
            <ExpandableContent isOpen={enabled}>
                {isPlacingCircle ? <AdsbPlacementControls /> : <AdsbActivePanel />}
                {circleCenter && (
                    <div className="px-3 py-2">
                        <ButtonSecondary
                            title="Zoom To Area"
                            className="w-full"
                            LeftIcon={Icons.ZoomTo}
                            onClick={() => zoomToAdsbCircle(circleCenter)}
                        />
                    </div>
                )}
            </ExpandableContent>
        </div>
    )
}

const AdsbPlacementControls = () => {
    const dispatch = useAppDispatch()
    return (
        <div className="flex flex-col gap-2 px-3">
            <div className="text-xs text-gray-300">
                Drag the circle on the map to set where ADS-B traffic is tracked, then confirm.
            </div>
            <div className="flex gap-2">
                <ButtonSecondary
                    title="Cancel"
                    className="flex-1"
                    onClick={() => dispatch(cancelAdsbCirclePlacement())}
                />
                <ButtonPrimary
                    title="Confirm"
                    className="flex-1"
                    onClick={() => dispatch(confirmAdsbCircle())}
                />
            </div>
        </div>
    )
}

const AdsbLicenseUpgradeModal = ({ cancel }: { cancel: () => void }) => {
    const dispatch = useAppDispatch()
    const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("annual")
    const currentLicense = useAppSelector(selectUserLicense)
    const currentLicenseType =
        !currentLicense || currentLicense.subscriptionType === Licenses.Lite
            ? Licenses.Lite
            : Licenses.Essentials
    const openLicenseUpgradeModal = useCallback(() => {
        dispatch(setLicenseUpgrade(currentLicense))
        dispatch(initializeCenterModalForComponentAction(ModalNames.LicenseUpgradeModal))
        cancel()
    }, [cancel, currentLicense, dispatch])
    return (
        <HighPriorityNovaModalContainer title="Upgrade license" closeModal={cancel}>
            <div className="flex flex-col items-center gap-[18px] p-2.5">
                <div className="flex flex-col gap-2.5 items-center">
                    <div className="text-[22px] text-white">
                        Upgrade to unlock live aircraft tracking
                    </div>
                    <div className="text-white">This feature is exclusive to Pro licenses.</div>
                    <div className="flex items-center justify-center gap-2.5">
                        <div
                            className={classNames({
                                "text-gray-50": billingCycle === "monthly",
                                "text-gray-400": billingCycle === "annual",
                            })}
                        >
                            Monthly
                        </div>
                        <ButtonToggle
                            initialValue
                            onToggle={() =>
                                setBillingCycle((prev) =>
                                    prev === "annual" ? "monthly" : "annual"
                                )
                            }
                        />
                        <div
                            className={classNames({
                                "text-gray-50": billingCycle === "annual",
                                "text-gray-400": billingCycle === "monthly",
                            })}
                        >
                            Annual
                        </div>
                    </div>
                    <div className="text-white">Save 25% with annual billing</div>
                </div>
                <div className="flex items-center gap-5">
                    <LicenseCard
                        defintion={LicenseDefinitions[currentLicenseType]}
                        currentLicense={currentLicense}
                        isAnnual={billingCycle === "annual"}
                        isAtLimit={false}
                        isCurrent
                        isPro={false}
                        openLicenseUpgradeModal={openLicenseUpgradeModal}
                    />
                    <LicenseCard
                        defintion={LicenseDefinitions[Licenses.Pro]}
                        currentLicense={currentLicense}
                        isAnnual={billingCycle === "annual"}
                        isAtLimit={false}
                        isCurrent={false}
                        isPro
                        openLicenseUpgradeModal={openLicenseUpgradeModal}
                    />
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const AdsbActivePanel = () => {
    const dispatch = useAppDispatch()
    const trafficUpdatesEnabled = useAppSelector(selectAdsbTrafficUpdatesEnabled)
    const altitudeMin = useAppSelector(selectAdsbTrafficUpdatesAltitudeMinFeet)
    const altitudeMax = useAppSelector(selectAdsbTrafficUpdatesAltitudeMaxFeet)
    const altitudeUnit = useAppSelector(selectAdsbTrafficUpdatesAltitudeUnit)
    const circleCenter = useAppSelector(selectAdsbCircleCenter)

    const updateShape = () => {
        if (circleCenter) zoomToAdsbCircle(circleCenter)
        dispatch(beginAdsbCirclePlacement())
    }

    return (
        <div className="flex flex-col">
            {adsbOptions.map(({ id, icon, title }) => (
                <AdsbOptionRow key={id} id={id} icon={icon} title={title} />
            ))}
            <div
                className="px-4 py-[14px] flex items-center justify-between cursor-pointer hover:bg-white-100"
                onClick={() => dispatch(setAdsbTrafficUpdatesEnabled(!trafficUpdatesEnabled))}
            >
                <div className="telegraf select-none">Traffic Updates</div>
                <ToggleSwitch isToggled={trafficUpdatesEnabled} setIsToggled={() => {}} />
            </div>
            <ExpandableContent isOpen={trafficUpdatesEnabled}>
                <div className="flex flex-col gap-0.5 pt-1 px-4 pb-3">
                    <div className="flex justify-between items-center">
                        <small className="text-gray-200">Altitude Range</small>
                        <Dropdown>
                            <Dropdown.Trigger>
                                <div className="flex gap-1 items-center text-gray-200 hover:text-gray-100">
                                    <small>{formattedDistanceUnit[altitudeUnit].abbrev}</small>
                                    <Icons.SmallIcon Icon={Icons.ArrowDropdown} />
                                </div>
                            </Dropdown.Trigger>
                            <Dropdown.Menu position="bottom-right">
                                <div className="py-1 bg-gray-700 flex flex-col w-[170px]">
                                    {ADSB_ALTITUDE_UNIT_OPTIONS.map((option) => (
                                        <Dropdown.ButtonItem
                                            key={option}
                                            onClick={() =>
                                                dispatch(setAdsbTrafficUpdatesAltitudeUnit(option))
                                            }
                                        >
                                            <div className="px-3 py-1.5 cursor-pointer">
                                                <small className="text-white">
                                                    {formattedDistanceUnit[option].full}
                                                </small>
                                            </div>
                                        </Dropdown.ButtonItem>
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div className="flex items-center gap-2">
                        <AltitudeInput
                            valueFeet={altitudeMin}
                            unit={altitudeUnit}
                            onCommitFeet={(value) =>
                                dispatch(setAdsbTrafficUpdatesAltitudeMinFeet(value))
                            }
                        />
                        <small className="text-gray-300">to</small>
                        <AltitudeInput
                            valueFeet={altitudeMax}
                            unit={altitudeUnit}
                            onCommitFeet={(value) =>
                                dispatch(setAdsbTrafficUpdatesAltitudeMaxFeet(value))
                            }
                        />
                    </div>
                </div>
            </ExpandableContent>
            <div className="px-3 pt-1">
                <ButtonTertiary
                    title="Update Shape"
                    className="w-full"
                    onClick={updateShape}
                    LeftIcon={Icons.Edit}
                />
            </div>
        </div>
    )
}

const AltitudeInput = ({
    valueFeet,
    unit,
    onCommitFeet,
}: {
    valueFeet: number
    unit: DistanceUnit
    onCommitFeet: (valueFeet: number) => void
}) => {
    const displayValue = Math.round(convertDistance(valueFeet, DistanceUnit.Feet, unit))
    const [draft, setDraft] = useState(String(displayValue))

    useEffect(() => {
        setDraft(String(displayValue))
    }, [displayValue])

    const commit = () => {
        const parsed = Number(draft)
        if (draft.trim() !== "" && Number.isFinite(parsed)) {
            onCommitFeet(Math.round(convertDistance(parsed, unit, DistanceUnit.Feet)))
        } else {
            setDraft(String(displayValue))
        }
    }

    return (
        <div className="shrink min-w-0">
            <InputOutlined
                className="w-full shrink"
                size="sm"
                type="text"
                inputMode="numeric"
                rightItem={formattedDistanceUnit[unit].abbrev}
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key !== "Enter") return
                    commit()
                    e.currentTarget.blur()
                }}
            />
        </div>
    )
}

const AdsbOptionRow = ({
    id,
    icon,
    title,
}: {
    id: AircraftTypeKey
    icon: Icons.IconComponent
    title: string
}) => {
    const dispatch = useAppDispatch()
    const isVisible = useAppSelector(selectAircraftTypeVisible(id))
    return (
        <div className="w-full p-3 flex items-center gap-2 group/option">
            <Icons.SmallIcon Icon={icon} className="text-gray-100" />
            <div
                className={classNames("text-xs select-none", {
                    "text-gray-50 group-hover/option:text-white": isVisible,
                    "text-gray-400 group-hover/option:text-gray-200": !isVisible,
                })}
            >
                {title}
            </div>
            <div className="grow" />
            <div
                className="w-5 h-5 shrink-0 flex justify-center items-center group/visibility hover:bg-gray-700 cursor-pointer rounded-md"
                onClick={() => dispatch(setAircraftTypeVisible({ key: id, value: !isVisible }))}
            >
                <Icons.ExtraSmallIcon
                    Icon={isVisible ? Icons.VisibilityEye : Icons.VisibilityEyeOff}
                    className={classNames({
                        "text-gray-300 group-hover/visibility:text-gray-100 opacity-0 group-hover/option:opacity-100":
                            isVisible,
                        "text-gray-500 group-hover/visibility:text-gray-300": !isVisible,
                    })}
                />
            </div>
        </div>
    )
}
